import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import {
    ChevronDown, ChevronRight, Calendar, Users, Flag, FolderKanban,
    Clock, Target, CheckCircle2, X, Building2, ExternalLink,
    GanttChart, Plus, Edit3, Play, Square
} from 'lucide-react';
import { format, isPast, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';

const ProjectListAsana = ({
    projects,
    users,
    customers,
    projectTasks,
    onProjectSelect,
    onEditProject,
    onTaskToggle,
    onCreateTask,
    onOpenTask,
    selectedProjectId
}) => {
    const [collapsedSections, setCollapsedSections] = useState({});

    const toggleSection = (sectionKey) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Group projects by customer
    const groupedProjects = useMemo(() => {
        const groups = {};
        
        projects.forEach(project => {
            const customerKey = project.customer_name || 'Ohne Kunde';
            if (!groups[customerKey]) {
                groups[customerKey] = {
                    label: customerKey,
                    projects: [],
                    customer_id: project.customer_id
                };
            }
            groups[customerKey].projects.push(project);
        });

        // Sort groups: projects with customers first, then "Ohne Kunde"
        return Object.entries(groups)
            .sort(([a], [b]) => {
                if (a === 'Ohne Kunde') return 1;
                if (b === 'Ohne Kunde') return -1;
                return a.localeCompare(b);
            });
    }, [projects]);

    // Calculate progress based on project tasks (not milestones)
    const getProjectProgress = (project) => {
        const tasks = projectTasks?.[project.id] || [];
        if (tasks.length === 0) return 0;
        const completed = tasks.filter(t => t.status === 'done' || t.status === 'erledigt').length;
        return Math.round((completed / tasks.length) * 100);
    };

    // Calculate total time spent on project
    const getProjectTime = (project) => {
        const tasks = projectTasks?.[project.id] || [];
        const totalSeconds = tasks.reduce((sum, t) => sum + (t.total_time_seconds || 0), 0);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        return { hours, minutes, totalSeconds };
    };

    const formatDeadline = (deadline) => {
        if (!deadline) return '';
        const date = new Date(deadline);
        const daysLeft = differenceInDays(date, new Date());
        if (daysLeft < 0) return `${Math.abs(daysLeft)} Tage überfällig`;
        if (daysLeft === 0) return 'Heute';
        if (daysLeft <= 7) return `${daysLeft} Tage`;
        return format(date, 'd. MMM', { locale: de });
    };

    return (
        <div className="flex h-[calc(100vh-200px)] bg-background rounded-xl border border-border overflow-hidden" data-testid="project-list-asana">
            {/* Left Panel - Project List */}
            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${selectedProjectId ? 'w-1/2' : 'w-full'}`}>
                {/* Table Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground sticky top-0 z-10">
                    <div className="flex-1 min-w-[200px]">Projekt</div>
                    <div className="w-24 text-center hidden md:block">Fortschritt</div>
                    <div className="w-20 text-center hidden md:block">Zeit</div>
                    <div className="w-24 text-center hidden md:block">Deadline</div>
                    <div className="w-20 text-center hidden lg:block">Tasks</div>
                    <div className="w-28 text-center hidden lg:block">Team</div>
                </div>

                {/* Project Groups */}
                <ScrollArea className="flex-1">
                    <div className="divide-y divide-border/50">
                        {groupedProjects.map(([customerName, group]) => {
                            // Color for customer section
                            const isNoCustomer = customerName === 'Ohne Kunde';
                            const sectionBg = isNoCustomer ? 'bg-muted/30' : 'bg-primary/5';
                            const sectionBorder = isNoCustomer ? 'border-l-4 border-l-gray-400' : 'border-l-4 border-l-primary';
                            const sectionText = isNoCustomer ? 'text-muted-foreground' : 'text-primary';
                            
                            return (
                            <div key={customerName} className="group/section">
                                {/* Section Header - Customer Name - Bigger and more colorful */}
                                <button
                                    onClick={() => toggleSection(customerName)}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 ${sectionBg} ${sectionBorder} hover:brightness-95 transition-all text-left`}
                                    data-testid={`section-${customerName}`}
                                >
                                    {collapsedSections[customerName] ? (
                                        <ChevronRight className={`w-5 h-5 ${sectionText}`} />
                                    ) : (
                                        <ChevronDown className={`w-5 h-5 ${sectionText}`} />
                                    )}
                                    <Building2 className={`w-5 h-5 ${sectionText}`} />
                                    <span className={`font-semibold text-base ${sectionText}`}>{customerName}</span>
                                    <Badge variant="secondary" className={`ml-2 text-sm font-medium ${sectionBg}`}>
                                        {group.projects.length}
                                    </Badge>
                                </button>

                                {/* Projects in Section */}
                                <AnimatePresence>
                                    {!collapsedSections[customerName] && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {group.projects.map((project) => (
                                                <ProjectRow
                                                    key={project.id}
                                                    project={project}
                                                    users={users}
                                                    isSelected={selectedProjectId === project.id}
                                                    onSelect={() => onProjectSelect(project)}
                                                    getProgress={getProjectProgress}
                                                    getProjectTime={getProjectTime}
                                                    formatDeadline={formatDeadline}
                                                    projectTasks={projectTasks}
                                                />
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            );
                        })}
                    </div>

                    {groupedProjects.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                            <FolderKanban className="w-12 h-12 mb-4 opacity-30" />
                            <p className="text-lg font-medium">Keine Projekte</p>
                            <p className="text-sm">Erstelle dein erstes Projekt!</p>
                        </div>
                    )}
                </ScrollArea>
            </div>

            {/* Right Panel - Project Details with Milestones */}
            <AnimatePresence>
                {selectedProjectId && (
                    <ProjectDetailPanel
                        project={projects.find(p => p.id === selectedProjectId)}
                        users={users}
                        projectTasks={projectTasks}
                        onClose={() => onProjectSelect(null)}
                        onEditProject={onEditProject}
                        onTaskToggle={onTaskToggle}
                        onCreateTask={onCreateTask}
                        onOpenTask={onOpenTask}
                        getProgress={getProjectProgress}
                        getProjectTime={getProjectTime}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

// Project Row Component
const ProjectRow = ({
    project,
    users,
    isSelected,
    onSelect,
    getProgress,
    getProjectTime,
    formatDeadline,
    projectTasks
}) => {
    const progress = getProgress(project);
    const tasks = projectTasks?.[project.id] || [];
    const completedTasks = tasks.filter(t => t.status === 'done' || t.status === 'erledigt').length;
    const isOverdue = project.deadline && isPast(new Date(project.deadline)) && progress < 100;
    const teamMembers = project.team_members || [];
    const time = getProjectTime(project);

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`flex items-center gap-2 px-4 py-3 border-b border-border/30 hover:bg-muted/30 transition-all cursor-pointer group
                ${isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : ''}
            `}
            onClick={onSelect}
            data-testid={`project-row-${project.id}`}
        >
            {/* Project Color Indicator */}
            <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.primary_color }}
            />

            {/* Project Name */}
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
                <span className="font-medium truncate">{project.name}</span>
                {project.is_archived && (
                    <Badge variant="secondary" className="text-xs">Archiviert</Badge>
                )}
            </div>

            {/* Progress */}
            <div className="w-24 hidden md:flex items-center gap-2">
                <Progress value={progress} className="h-2 flex-1" />
                <span className="text-xs text-muted-foreground w-8">{progress}%</span>
            </div>

            {/* Time */}
            <div className="w-20 text-center text-sm hidden md:block text-muted-foreground">
                {time.totalSeconds > 0 ? (
                    <span className="font-mono text-xs">{time.hours}h {time.minutes}m</span>
                ) : (
                    <span className="text-muted-foreground/50">-</span>
                )}
            </div>

            {/* Deadline */}
            <div className={`w-24 text-center text-sm hidden md:block ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                {project.deadline ? (
                    <span className={`px-2 py-1 rounded ${isOverdue ? 'bg-red-500/10' : ''}`}>
                        {formatDeadline(project.deadline)}
                    </span>
                ) : (
                    <span className="text-muted-foreground/50">-</span>
                )}
            </div>

            {/* Tasks Count */}
            <div className="w-20 text-center hidden lg:block">
                <Badge variant="outline" className="text-xs">
                    {completedTasks}/{tasks.length}
                </Badge>
            </div>

            {/* Team */}
            <div className="w-28 hidden lg:flex justify-center">
                {teamMembers.length > 0 ? (
                    <div className="flex -space-x-2">
                        {teamMembers.slice(0, 3).map((memberId, i) => {
                            const member = users?.find(u => u.id === memberId);
                            if (!member) return null;
                            return (
                                <Avatar key={memberId} className="w-6 h-6 border-2 border-background">
                                    <AvatarFallback 
                                        style={{ backgroundColor: member.profile_color }} 
                                        className="text-white text-[10px]"
                                    >
                                        {member.first_name?.[0]}{member.last_name?.[0]}
                                    </AvatarFallback>
                                </Avatar>
                            );
                        })}
                        {teamMembers.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-background">
                                +{teamMembers.length - 3}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="text-muted-foreground/50 text-sm">-</span>
                )}
            </div>
        </motion.div>
    );
};

// Project Detail Panel with Tasks
const ProjectDetailPanel = ({
    project,
    users,
    projectTasks,
    onClose,
    onEditProject,
    onTaskToggle,
    onCreateTask,
    onOpenTask,
    getProgress,
    getProjectTime
}) => {
    const [collapsedTasks, setCollapsedTasks] = useState({});

    if (!project) return null;

    const tasks = projectTasks?.[project.id] || [];
    const progress = getProgress(project);
    const time = getProjectTime(project);
    const isOverdue = project.deadline && isPast(new Date(project.deadline)) && progress < 100;
    const teamMembers = (project.team_members || [])
        .map(id => users?.find(u => u.id === id))
        .filter(Boolean);

    // Group tasks by status
    const pendingTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'erledigt');
    const completedTasks = tasks.filter(t => t.status === 'done' || t.status === 'erledigt');

    return (
        <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '450px', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="border-l border-border bg-card flex flex-col h-full"
            data-testid="project-detail-panel"
        >
            {/* Panel Header with Edit Button */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border" style={{ backgroundColor: project.primary_color + '20' }}>
                <div className="flex items-center gap-2">
                    <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: project.primary_color }}
                    />
                    <span className="font-medium truncate max-w-[250px]">{project.name}</span>
                </div>
                <div className="flex items-center gap-1">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8" 
                        onClick={() => onEditProject?.(project)}
                        data-testid="edit-project-btn"
                    >
                        <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                    {/* Progress & Time Overview */}
                    <div className="bg-muted/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">Fortschritt</span>
                            <span className="text-sm text-muted-foreground">{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-3" />
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                            <div className="text-xs text-muted-foreground">
                                {completedTasks.length} von {tasks.length} Tasks erledigt
                            </div>
                            <div className="flex items-center gap-1 text-xs font-mono">
                                <Clock className="w-3 h-3" />
                                {time.hours}h {time.minutes}m
                            </div>
                        </div>
                    </div>

                    {/* Project Info */}
                    <div className="space-y-3">
                        {/* Customer */}
                        {project.customer_name && (
                            <div className="flex items-center gap-2 text-sm">
                                <Building2 className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Kunde:</span>
                                <span className="font-medium">{project.customer_name}</span>
                            </div>
                        )}

                        {/* Deadline */}
                        {project.deadline && (
                            <div className={`flex items-center gap-2 text-sm ${isOverdue ? 'text-red-500' : ''}`}>
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Deadline:</span>
                                <span className={isOverdue ? 'font-medium' : ''}>
                                    {format(new Date(project.deadline), 'PPP', { locale: de })}
                                </span>
                            </div>
                        )}

                        {/* Team */}
                        {teamMembers.length > 0 && (
                            <div className="flex items-start gap-2 text-sm">
                                <Users className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <span className="text-muted-foreground">Team:</span>
                                <div className="flex flex-wrap gap-1">
                                    {teamMembers.map(member => (
                                        <Badge key={member.id} variant="secondary" className="text-xs">
                                            {member.first_name} {member.last_name?.[0]}.
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        {project.description && (
                            <div className="text-sm">
                                <p className="text-muted-foreground mb-1">Beschreibung:</p>
                                <p className="bg-muted/50 rounded p-2">{project.description}</p>
                            </div>
                        )}
                    </div>

                    {/* Tasks Section (Meilensteine) */}
                    <div className="border-t border-border pt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium flex items-center gap-2">
                                <Target className="w-4 h-4" />
                                Meilensteine ({tasks.length})
                            </h3>
                            <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => onCreateTask?.(project.id)}
                                data-testid="create-project-task-btn"
                            >
                                <Plus className="w-3 h-3 mr-1" />
                                Task hinzufügen
                            </Button>
                        </div>

                        {/* Pending Tasks */}
                        {pendingTasks.length > 0 && (
                            <div className="mb-4">
                                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">
                                    Offen ({pendingTasks.length})
                                </p>
                                <div className="space-y-2">
                                    {pendingTasks.map(task => (
                                        <TaskRow
                                            key={task.id}
                                            task={task}
                                            users={users}
                                            onToggle={onTaskToggle}
                                            onClick={() => onOpenTask?.(task)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Completed Tasks */}
                        {completedTasks.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setCollapsedTasks(prev => ({ ...prev, completed: !prev.completed }))}
                                    className="flex items-center gap-1 text-xs text-muted-foreground mb-2 uppercase tracking-wide hover:text-foreground"
                                >
                                    {collapsedTasks.completed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    Erledigt ({completedTasks.length})
                                </button>
                                {!collapsedTasks.completed && (
                                    <div className="space-y-2">
                                        {completedTasks.map(task => (
                                            <TaskRow
                                                key={task.id}
                                                task={task}
                                                users={users}
                                                onToggle={onTaskToggle}
                                                onClick={() => onOpenTask?.(task)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {tasks.length === 0 && (
                            <div className="text-center py-6 text-muted-foreground">
                                <GanttChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                <p className="text-sm">Keine Meilensteine für dieses Projekt</p>
                                <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="mt-2"
                                    onClick={() => onCreateTask?.(project.id)}
                                >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Ersten Task erstellen
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </ScrollArea>
        </motion.div>
    );
};

// Task Row Component (for project tasks)
const TaskRow = ({ task, users, onToggle, onClick }) => {
    const isOverdue = task.deadline && isPast(new Date(task.deadline)) && task.status !== 'done' && task.status !== 'erledigt';
    const isDone = task.status === 'done' || task.status === 'erledigt';
    const assignee = task.assigned_users?.[0];

    return (
        <div 
            className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                isDone ? 'bg-muted/30' : 'bg-background border border-border hover:border-primary/30'
            }`}
            onClick={onClick}
        >
            <Checkbox
                checked={isDone}
                onCheckedChange={(checked) => {
                    onToggle?.(task.id, checked ? 'done' : 'open');
                }}
                onClick={(e) => e.stopPropagation()}
                className="mt-0.5 rounded-full"
                data-testid={`task-checkbox-${task.id}`}
            />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className={`font-medium text-sm ${isDone ? 'line-through text-muted-foreground' : ''}`}>
                        {task.title}
                    </p>
                    {task.priority === 1 && (
                        <Flag className="w-3 h-3 text-red-500" />
                    )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {task.deadline && (
                        <span className={`text-xs ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`}>
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {format(new Date(task.deadline), 'd. MMM', { locale: de })}
                        </span>
                    )}
                    {assignee && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Avatar className="w-4 h-4">
                                <AvatarFallback 
                                    style={{ backgroundColor: assignee.profile_color }} 
                                    className="text-white text-[8px]"
                                >
                                    {assignee.first_name?.[0]}
                                </AvatarFallback>
                            </Avatar>
                            {assignee.first_name}
                        </span>
                    )}
                    {task.total_time_seconds > 0 && (
                        <span className="text-xs text-muted-foreground font-mono">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {Math.floor(task.total_time_seconds / 3600)}h
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectListAsana;
